import React, { useState, useEffect } from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';

import {
  FlowProgressHeader,
  StepMatch,
} from 'views/components/FlowProgressHeader/FlowProgressHeader';
import { FlowButtonBar } from 'views/components/FlowButtonBar/FlowButtonBar';
import { SelectInput } from 'views/components/SelectInput/SelectInput';
import {
  IPayRun, IWorkItOutData, PayCycleOptions, PayCycleTypes, Bank,
} from 'types';
import { actionSaveStepWorkItOut } from 'store/modules/sign-up/actions';
import { getErrorMessageFor } from 'tools/utilities/getErrorMessageFor';
import { RadioInput } from 'views/components/RadioInput/RadioInput';
import { PricingPlaceholder } from 'views/components/Pricing/PricingPlaceholder';
import { scrollToError } from 'tools/utilities/scrollToError';
import { TextInput } from 'views/components/TextInput/TextInput';
import { PayRuns, MAX_EMPLOYEE_COUNT } from './PayRuns/PayRuns';

interface WorkItOutPageProps {
  intl: IntlShape;
  bankOptions: Bank[];
  manageHrOptions: string[];
  managePayCycleOptions: PayCycleOptions[];
  initialData: IWorkItOutData;
  saveStepWorkItOut: typeof actionSaveStepWorkItOut;
  goToNextStep: () => void;
  payCycles: string[];
}

export const WorkItOutView = ({
  intl,
  bankOptions = [],
  manageHrOptions = [],
  managePayCycleOptions = [],
  initialData,
  saveStepWorkItOut,
  goToNextStep,
  payCycles,
}: WorkItOutPageProps) => {
  const [touchedNext, setTouchedNext] = useState(false);

  // Set initial values based on values saved in the redux store
  const [payRuns, setPayRuns] = useState<IPayRun[]>(initialData.payRuns);
  const [managePayCycles, setManagePayCycles] = useState(initialData.managePayCycles);
  const [manageHr, setManageHr] = useState(initialData.manageHr);
  const [bankChoice, setBankChoice] = useState<Bank | undefined>(initialData.bankChoice);
  const [bankOther, setBankOther] = useState(initialData.bankOther || '');

  useEffect(() => {
    // If the bankChoice changes and it's not option other then clear bankOther
    // Could check ID here - Bank Other ID ""
    if (!bankChoice?.isBankOther) {
      setBankOther('');
    }
  }, [bankChoice]);

  // At least the first payRun should have an employeeCount and payCycle and the rest can have both or no employeeCount and payCycle
  const payRunsIsValid = (payRuns.length > 0)
    // User should have at least 1 payRun filled out
    && (payRuns[0].employeeCount && payRuns[0].employeeCount <= MAX_EMPLOYEE_COUNT && !!payRuns[0].payCycle)
    && payRuns.every(({ employeeCount, payCycle }) => (employeeCount && employeeCount <= MAX_EMPLOYEE_COUNT && !!payCycle)
    || ((!employeeCount || employeeCount === 0) && !payCycle));

  const managePayCyclesIsValid = !!managePayCycles;
  const bankChoiceIsValid = !!bankChoice;
  const bankOtherIsValid = (!bankChoice?.isBankOther) || (bankChoice?.isBankOther && !!bankOther);

  const isValid = payRunsIsValid && managePayCyclesIsValid && bankChoiceIsValid && bankOtherIsValid;

  const onNext = () => {
    setTouchedNext(true);

    if (!isValid) {
      scrollToError();
      return;
    }

    if (bankChoice) {
      const workItOutData = {
        payRuns,
        managePayCycles,
        manageHr,
        bankChoice,
        bankOther,
        payCycles,
      };

      // Save work it out step data
      saveStepWorkItOut(workItOutData);

      // Go to next step
      goToNextStep();
    }
  };

  return (
    <div className="WorkItOut">
      <FlowProgressHeader flowMatch={StepMatch.WorkItOut} />

      <div className="Container">

        <div className="Grid">
          {/* Left */}
          <div className="Grid-cell u-width1of1 u-md-width1of2 u-lg-width7of12 u-xl-width2of3">
            <div className="Tile">
              <h2>
                <FormattedMessage id="HOW_MANY_EMPLOYEES" />
              </h2>

              <div className="WorkItOut-group">
                <PayRuns
                  payCycles={payCycles}
                  initialValue={payRuns}
                  onChange={setPayRuns}
                  touchedNext={touchedNext}
                  noPayRunErrorMessage={intl.formatMessage({ id: 'ERROR_NO_PAY_RUN' })}
                  employeeCountErrorMessage={intl.formatMessage({ id: 'ERROR_EMPLOYEE_COUNT' })}
                  payCycleErrorMessage={intl.formatMessage({ id: 'ERROR_PAY_CYCLE' })}
                  employeeMaxErrorMessage={intl.formatMessage({ id: 'ERROR_MAX_EMPLOYEE_COUNT' })}
                />
              </div>

              <div className="WorkItOut-divider" />

              <div className="WorkItOut-group">
                <RadioInput
                  id="managePayCycles"
                  onChange={(val) => setManagePayCycles(val as PayCycleTypes)}
                  defaultValue={managePayCycles}
                  options={managePayCycleOptions.map(({ id }) => id)}
                  error={touchedNext && getErrorMessageFor(managePayCyclesIsValid, 'ERROR_MANAGE_PAY_CYCLES', intl)}
                  renderLabelFor={(index) => {
                    const { title, label } = managePayCycleOptions[index];
                    return (
                      <>
                        <span className="WorkItOut-option-title">{title}</span>
                        <span className="WorkItOut-option-label">{label}</span>
                      </>
                    );
                  }}
                >
                  <span className="WorkItOut-group-label">
                    <FormattedMessage tagName="h2" id="MANAGE_PAY_CYCLES" />
                    <FormattedMessage tagName="p" id="MANAGE_PAY_CYCLES_TEXT" />
                  </span>
                </RadioInput>
              </div>

              <div className="WorkItOut-divider" />

              <div className="WorkItOut-group">
                <SelectInput
                  isRequired={false}
                  id="manageHr"
                  placeholder={intl.formatMessage({ id: 'SELECT' })}
                  onChange={setManageHr}
                  options={manageHrOptions}
                  defaultValue={manageHr}
                >
                  <span className="WorkItOut-group-label">
                    <FormattedMessage tagName="h2" id="MANAGE_HR_SERVICES" />
                    <FormattedMessage tagName="p" id="MANAGE_HR_SERVICES_TEXT" />
                  </span>
                </SelectInput>
              </div>

              <div className="WorkItOut-divider" />

              <div className="WorkItOut-group">
                <SelectInput
                  id="bank"
                  placeholder={intl.formatMessage({ id: 'SELECT' })}
                  onChange={(val) => {
                    const bank = bankOptions.find(({ name }) => val === name);
                    setBankChoice(bank);
                  }}
                  options={bankOptions.map((bank) => bank.name)}
                  defaultValue={bankChoice?.name}
                  error={touchedNext && getErrorMessageFor(bankChoiceIsValid, 'ERROR_WHO_DO_YOU_BANK_WITH', intl)}
                >
                  <span className="WorkItOut-group-label">
                    <FormattedMessage tagName="h2" id="WHO_DO_YOU_BANK_WITH" />
                    <FormattedMessage tagName="p" id="BANK_WHY" />
                  </span>
                </SelectInput>

                {bankChoice?.isBankOther && (
                  <TextInput
                    id="bank-other"
                    type="text"
                    placeholder={intl.formatMessage({ id: 'BANK_PLACEHOLDER' })}
                    label={intl.formatMessage({ id: 'BANK_OTHER' })}
                    error={touchedNext && getErrorMessageFor(bankOtherIsValid, 'ERROR_BANK_OTHER', intl)}
                    onChange={setBankOther}
                    value={bankOther || ''}
                  />
                )}
              </div>

              <FlowButtonBar
                onPrimary={() => onNext()}
              />
            </div>
          </div>

          {/* Right */}
          <div className="Grid-cell u-width1of1 u-md-width1of2 u-lg-width5of12 u-xl-width1of3">
            <PricingPlaceholder />
          </div>
        </div>
      </div>
    </div>
  );
};
